/**
 * Pure functions for building subdomain-based URLs, resolving base domains, and extracting event subdomains from hostnames.
 * Isomorphic — safe to use in both frontend and backend.
 */

import { InternalServerError } from "../errors/base.error.js";
import { EventIdPublic, EventSlug } from "../schemas/branded-id.schemas.js";

export class InvalidSubdomainLabelError extends InternalServerError {
  constructor(label: string) {
    super(SubdomainErrors.invalidDnsLabel(label));
  }
}

const SubdomainErrors = {
  invalidDnsLabel: (label: string): string =>
    `Invalid eventIdPublic for subdomain: "${label}" is not a valid DNS label`,
} as const;

/**
 * Known base domains, ordered longest-first.
 * Longest-first ordering is critical for `.endsWith()` matching:
 * `dev2.planta.network` must match before `planta.network`.
 */
export const KNOWN_BASE_DOMAINS = [
  "dev2.planta.network",
  "planta.network",
  "lvh.me",
  "planta.local",
] as const;

const HTTP_DOMAINS: ReadonlySet<string> = new Set(["lvh.me", "planta.local"]);

/**
 * Valid DNS label: 1-63 chars, alphanumeric or hyphens, cannot start/end with hyphen.
 */
const DNS_LABEL_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

/**
 * Subdomains that are reserved for infrastructure and must not be treated as event IDs.
 */
const RESERVED_SUBDOMAINS: ReadonlySet<string> = new Set([
  "www",
  "api",
  "app",
  "admin",
  "host",
  "dev2",
]);

/**
 * Builds a subdomain URL for an event.
 * Prefers eventSlug as the subdomain label when provided and DNS-valid; otherwise falls back to eventIdPublic.
 * @param frontendBaseUrl - The frontend base URL (e.g., "https://app.planta.network" or "http://app.lvh.me:8080")
 * @param eventIdPublic - The public event ID (used as subdomain fallback; must be a valid DNS label)
 * @param eventSlug - Optional event slug; used as subdomain if DNS-valid, silently falls back to eventIdPublic otherwise
 * @returns The subdomain URL (e.g., "https://my-event.planta.network")
 * @throws InvalidSubdomainLabelError if the resolved label is not a valid DNS label
 */
export function buildSubdomainUrl(
  frontendBaseUrl: string,
  eventIdPublic: EventIdPublic,
  eventSlug?: EventSlug | null,
): string {
  const slugValue = eventSlug != null ? EventSlug.unwrap(eventSlug) : null;
  const rawId =
    slugValue !== null && DNS_LABEL_RE.test(slugValue)
      ? slugValue
      : EventIdPublic.unwrap(eventIdPublic);
  if (!DNS_LABEL_RE.test(rawId)) {
    throw new InvalidSubdomainLabelError(rawId);
  }
  const url = new URL(frontendBaseUrl);
  const hostname = url.hostname;

  for (const baseDomain of KNOWN_BASE_DOMAINS) {
    if (hostname === baseDomain || hostname.endsWith(`.${baseDomain}`)) {
      const protocol = HTTP_DOMAINS.has(baseDomain) ? "http:" : "https:";
      const port = url.port !== "" ? `:${url.port}` : "";
      return `${protocol}//${rawId}.${baseDomain}${port}`;
    }
  }

  const port = url.port !== "" ? `:${url.port}` : "";
  return `${url.protocol}//${rawId}.${hostname}${port}`;
}

/**
 * Given a hostname (potentially with a subdomain), returns the full base domain URL.
 * @param hostname - The hostname, possibly with port (e.g., "myevent.planta.network", "myevent.lvh.me:8080")
 * @returns The base domain URL (e.g., "https://planta.network") or null for unknown domains
 */
export function buildBaseDomainUrl(hostname: string): string | null {
  const [host, port] = hostname.split(":");

  if (host === undefined || host === "") {
    return null;
  }

  for (const baseDomain of KNOWN_BASE_DOMAINS) {
    if (host === baseDomain || host.endsWith(`.${baseDomain}`)) {
      const protocol = HTTP_DOMAINS.has(baseDomain) ? "http:" : "https:";
      const portSuffix = port !== undefined ? `:${port}` : "";
      return `${protocol}//${baseDomain}${portSuffix}`;
    }
  }

  return null;
}

export function getEventSubdomainFromHostname(hostname: string): string | null {
  const host = hostname.split(":")[0] ?? "";

  for (const baseDomain of KNOWN_BASE_DOMAINS) {
    if (!host.endsWith(`.${baseDomain}`)) {
      continue;
    }

    const prefix = host.slice(0, -(baseDomain.length + 1)).toLowerCase();

    if (
      prefix.length === 0 ||
      prefix.includes(".") ||
      RESERVED_SUBDOMAINS.has(prefix)
    ) {
      return null;
    }

    return prefix;
  }

  return null;
}

export function stripEventPrefix(
  path: string,
  eventIdPublic: EventIdPublic,
  eventSlug: EventSlug | null | undefined,
): string {
  const identifiers = [
    eventSlug != null ? EventSlug.unwrap(eventSlug) : null,
    EventIdPublic.unwrap(eventIdPublic),
  ].filter((v): v is string => v !== null);

  for (const identifier of identifiers) {
    const eventPrefix = `/e/${identifier}`;
    if (path.startsWith(eventPrefix)) {
      const restOfPath = path.substring(eventPrefix.length);
      if (restOfPath === "" || restOfPath.startsWith("/")) {
        return restOfPath !== "" ? restOfPath : "/";
      }
    }
  }

  return path;
}
